/**
 * Maps an array to a single object.
 * Each item is mapped to a key-value, and each key-value is
 * mapped to the return object by the lambda.
 * Each key can have only one value, thus there can be no duplicates.
 *
 * For example:
 *
 * 	mapToObject(['Apple','Avocado','Banana'], x => ({[x[0]]: x.toUpperCase()}))
 * 	// Result = {'A':'AVOCADO','B':'BANANA'}
 *
 * @param arr Source array
 * @param mapper Map an item to a key-value
 */
export const mapToObject = <T1, T2>(
  arr: T1[],
  mapper?: (x: T1, i: number) => { [key: string]: T2 }
): { [key: string]: T2 } => {
  if (!mapper) mapper = (a) => a as any; // support for flattening an array of objects

  const obj = {};

  for (const partial of arr.map(mapper)) {
    if (!partial) continue;

    for (const key in partial) {
      // Merge if it can
      if (obj[key] && partial[key]) {
        if (_isObject(obj[key]) && _isObject(partial[key])) {
          obj[key] = {
            ...obj[key],
            ...partial[key],
          };
          continue;
        } else if (Array.isArray(obj[key]) && Array.isArray(partial[key])) {
          obj[key] = [...obj[key], ...(partial[key] as any)];
          continue;
        }
      }

      // Otherwise just assign
      obj[key] = partial[key];
    }
  }

  return obj;
};

const _isObject = (item: any): item is object => {
  return item && typeof item === 'object' && !Array.isArray(item);
};
